import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../config";
import { useParams, Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, Zoom } from "swiper/modules";
import ReviewsSection from "./ReviewsSection";
import {
    ShoppingCart, Heart, ChevronLeft, Truck, Shield, RotateCcw,
    Check, AlertCircle, Star, Package, CreditCard, Store,
    ChevronRight, Minus, Plus, Copy, CheckCircle2
} from "lucide-react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/zoom";

const ProductDetails = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState({});
    const [productImages, setProductImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addingToCart, setAddingToCart] = useState(false);
    const [added, setAdded] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [isFavorite, setIsFavorite] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const fetchProductDetails = async () => {
            try {
                const productResponse = await fetch(
                    `${API_BASE_URL}/api/products/details/${productId}`
                );
                if (!productResponse.ok) {
                    throw new Error(`HTTP error! status: ${productResponse.status}`);
                }
                const productData = await productResponse.json();
                setProduct(productData);

                // บันทึกประวัติการเข้าชมสินค้า
                const token = localStorage.getItem("token");
                if (token && productData.categoryId) {
                    try {
                        await fetch(`${API_BASE_URL}/api/recommendations/track-view`, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: token,
                            },
                            body: JSON.stringify({
                                productId: parseInt(productId),
                                categoryId: productData.categoryId,
                            }),
                        });
                    } catch (trackError) {
                        console.log("Track view error (non-critical):", trackError);
                    }

                    // ตรวจสอบสถานะ wishlist
                    try {
                        const favResponse = await fetch(
                            `${API_BASE_URL}/api/wishlist/check/${productId}`,
                            { headers: { Authorization: token } }
                        );
                        if (favResponse.ok) {
                            const favData = await favResponse.json();
                            setIsFavorite(favData.isInWishlist);
                        }
                    } catch (favError) {
                        console.log("Check wishlist error (non-critical):", favError);
                    }
                }

                const imageResponse = await fetch(
                    `${API_BASE_URL}/api/product-images/product/${productId}`
                );
                if (!imageResponse.ok) {
                    throw new Error(`HTTP error! status: ${imageResponse.status}`);
                }
                const imageData = await imageResponse.json();
                setProductImages(imageData);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        fetchProductDetails();
    }, [productId]);

    const addToCart = async () => {
        setAddingToCart(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/cart/add`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify({ productId: parseInt(productId), quantity }),
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            setAdded(true);
            setTimeout(() => setAdded(false), 2000);
        } catch (error) {
            console.error("Error adding to cart:", error);
            alert("เกิดข้อผิดพลาดในการเพิ่มสินค้าลงตะกร้า กรุณาลองใหม่");
        } finally {
            setAddingToCart(false);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            alert("คัดลอกลิงก์ไม่สำเร็จ");
        }
    };

    const toggleFavorite = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            alert("กรุณาเข้าสู่ระบบเพื่อบันทึกรายการถูกใจ");
            return;
        }

        try {
            if (isFavorite) {
                // ลบออกจาก wishlist
                const response = await fetch(
                    `${API_BASE_URL}/api/wishlist/${productId}`,
                    {
                        method: "DELETE",
                        headers: { Authorization: token },
                    }
                );
                if (response.ok) {
                    setIsFavorite(false);
                }
            } else {
                // เพิ่มใน wishlist
                const response = await fetch(
                    `${API_BASE_URL}/api/wishlist/${productId}`,
                    {
                        method: "POST",
                        headers: { Authorization: token },
                    }
                );
                if (response.ok) {
                    setIsFavorite(true);
                }
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    const incrementQuantity = () => {
        if (quantity < product.quantity) {
            setQuantity(q => q + 1);
        }
    };

    const decrementQuantity = () => {
        if (quantity > 1) {
            setQuantity(q => q - 1);
        }
    };

    // Loading State
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="lg:grid lg:grid-cols-12 lg:gap-8 animate-pulse">
                        <div className="lg:col-span-7">
                            <div className="aspect-square bg-gray-200 rounded-2xl" />
                            <div className="flex gap-3 mt-4">
                                {[1, 2, 3, 4].map(i => (
                                    <div key={i} className="w-20 h-20 bg-gray-200 rounded-lg" />
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-5 mt-8 lg:mt-0 space-y-4">
                            <div className="h-4 bg-gray-200 rounded w-1/4" />
                            <div className="h-8 bg-gray-200 rounded w-3/4" />
                            <div className="h-6 bg-gray-200 rounded w-1/3" />
                            <div className="h-12 bg-gray-200 rounded w-1/2 mt-4" />
                            <div className="h-4 bg-gray-200 rounded w-full mt-6" />
                            <div className="h-4 bg-gray-200 rounded w-5/6" />
                            <div className="h-14 bg-gray-200 rounded w-full mt-8" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
                <div className="text-center bg-white p-12 rounded-2xl shadow-lg">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">เกิดข้อผิดพลาด</h2>
                    <p className="text-gray-600 mb-6">{error.message}</p>
                    <Link to="/all-products" className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-600/25">
                        <ChevronLeft size={20} />
                        กลับไปหน้าสินค้า
                    </Link>
                </div>
            </div>
        );
    }

    // Not Found
    if (!product || Object.keys(product).length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100">
                <div className="text-center bg-white p-12 rounded-2xl shadow-lg">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Package className="w-10 h-10 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">ไม่พบสินค้านี้</h2>
                    <p className="text-gray-600 mb-6">สินค้าที่คุณกำลังมองหาอาจถูกลบหรือไม่มีอยู่จริง</p>
                    <Link to="/all-products" className="text-red-600 hover:text-red-700 font-medium">
                        กลับไปเลือกซื้อสินค้า &rarr;
                    </Link>
                </div>
            </div>
        );
    }

    const shopName = product.shopName || `${product.firstName || ''} ${product.lastName || ''}`.trim() || "ไม่ระบุชื่อร้านค้า";

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
            {/* Breadcrumb */}
            <div className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/all-products" className="inline-flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors group">
                            <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                            <span>ย้อนกลับ</span>
                        </Link>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Link to="/home" className="hover:text-red-600">หน้าแรก</Link>
                            <ChevronRight size={16} />
                            <Link to="/all-products" className="hover:text-red-600">สินค้าทั้งหมด</Link>
                            <ChevronRight size={16} />
                            <span className="text-gray-900 font-medium truncate max-w-[200px]">{product.productName}</span>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Main Product Section */}
                <div className="lg:grid lg:grid-cols-12 lg:gap-8">
                    {/* Image Gallery - Left Side */}
                    <div className="lg:col-span-7">
                        {/* Main Image */}
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
                            <Swiper
                                navigation={true}
                                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                modules={[Navigation, Thumbs, Zoom]}
                                className="aspect-square"
                                zoom={true}
                                loop={productImages.length > 1}
                            >
                                {productImages.length > 0 ? (
                                    productImages.map((image) => (
                                        <SwiperSlide key={image.imageId}>
                                            <div className="swiper-zoom-container">
                                                <img
                                                    src={`${API_BASE_URL}${image.imageUrl}`}
                                                    alt={product.productName}
                                                    className="w-full h-full object-contain bg-gray-50"
                                                />
                                            </div>
                                        </SwiperSlide>
                                    ))
                                ) : (
                                    <SwiperSlide>
                                        <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50">
                                            <Package className="w-24 h-24 text-gray-200" />
                                        </div>
                                    </SwiperSlide>
                                )}
                            </Swiper>
                        </div>

                        {/* Thumbnail Images */}
                        {productImages.length > 1 && (
                            <div className="mt-4">
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    spaceBetween={12}
                                    slidesPerView={5}
                                    watchSlidesProgress={true}
                                    className="thumbs-swiper"
                                >
                                    {productImages.map((image) => (
                                        <SwiperSlide key={image.imageId} className="cursor-pointer">
                                            <div className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-red-500 transition-all">
                                                <img
                                                    src={`${API_BASE_URL}${image.imageUrl}`}
                                                    alt=""
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        )}
                    </div>

                    {/* Product Info - Right Side */}
                    <div className="lg:col-span-5 mt-8 lg:mt-0">
                        <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm">
                            {/* Stock Badge */}
                            {product.quantity <= 5 && product.quantity > 0 && (
                                <div className="mb-4">
                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-50 text-amber-600 rounded-full text-sm font-medium">
                                        เหลือเพียง {product.quantity} ชิ้น
                                    </span>
                                </div>
                            )}

                            {/* Product Name */}
                            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight mb-4">
                                {product.productName}
                            </h1>

                            {/* Rating */}
                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <Star
                                            key={star}
                                            size={18}
                                            className={star <= Math.round(product.sellerRating || 0)
                                                ? "text-yellow-400 fill-yellow-400"
                                                : "text-gray-200 fill-gray-200"
                                            }
                                        />
                                    ))}
                                    <span className="ml-2 text-sm text-gray-600">
                                        {product.sellerRating?.toFixed(1) || "0.0"} ({product.reviewCount || 0} รีวิว)
                                    </span>
                                </div>
                            </div>

                            {/* Price */}
                            <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-6">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-4xl font-bold text-red-600">
                                        ฿{Number(product.price).toLocaleString()}
                                    </span>
                                </div>
                            </div>

                            {/* Stock Status */}
                            <div className="flex items-center gap-2 mb-6 text-sm">
                                <div className={`w-2 h-2 rounded-full ${product.quantity > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                                <span className={product.quantity > 0 ? 'text-green-600' : 'text-red-600'}>
                                    {product.quantity > 0 ? 'มีสินค้าพร้อมส่ง' : 'สินค้าหมด'}
                                </span>
                                {product.quantity > 0 && (
                                    <span className="text-gray-500">• พร้อมจัดส่ง: {product.quantity} ชิ้น</span>
                                )}
                            </div>

                            {/* Quantity Selector */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">จำนวน</label>
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center border border-gray-200 rounded-lg">
                                        <button
                                            onClick={decrementQuantity}
                                            disabled={quantity <= 1}
                                            className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <Minus size={18} />
                                        </button>
                                        <span className="w-16 text-center font-semibold text-lg">{quantity}</span>
                                        <button
                                            onClick={incrementQuantity}
                                            disabled={quantity >= product.quantity}
                                            className="p-3 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="space-y-3 mb-8">
                                <button
                                    onClick={addToCart}
                                    disabled={addingToCart || added || product.quantity <= 0}
                                    className={`w-full flex items-center justify-center gap-3 py-4 px-8 rounded-xl text-lg font-semibold transition-all shadow-lg ${added
                                        ? "bg-green-500 text-white shadow-green-500/25"
                                        : product.quantity <= 0
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed shadow-none"
                                            : "bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white shadow-red-500/25"
                                        }`}
                                >
                                    {addingToCart ? (
                                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : added ? (
                                        <>
                                            <CheckCircle2 size={24} />
                                            เพิ่มลงตะกร้าแล้ว!
                                        </>
                                    ) : product.quantity <= 0 ? (
                                        "สินค้าหมด"
                                    ) : (
                                        <>
                                            <ShoppingCart size={24} />
                                            เพิ่มลงตะกร้า
                                        </>
                                    )}
                                </button>

                                <div className="flex gap-3">
                                    <button
                                        onClick={toggleFavorite}
                                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border rounded-xl transition-all ${isFavorite
                                            ? "border-red-500 bg-red-50 text-red-600"
                                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                                            }`}
                                    >
                                        <Heart size={20} className={isFavorite ? "fill-red-500" : ""} />
                                        {isFavorite ? "เลิกชอบ" : "ถูกใจ"}
                                    </button>
                                    <button
                                        onClick={handleShare}
                                        className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-all"
                                    >
                                        {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} />}
                                        {copied ? "คัดลอกแล้ว" : "แชร์สินค้า"}
                                    </button>
                                </div>
                            </div>

                            {/* Features */}
                            <div className="grid grid-cols-2 gap-3 pt-6 border-t border-gray-100">
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <Truck className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">จัดส่งฟรี</p>
                                        <p className="text-xs text-gray-500">เมื่อสั่งซื้อครบ ฿500+</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                        <Shield className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">รับประกันของแท้</p>
                                        <p className="text-xs text-gray-500">สินค้าถูกลิขสิทธิ์ 100%</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <RotateCcw className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">คืนสินค้าได้ง่าย</p>
                                        <p className="text-xs text-gray-500">ภายใน 7 วัน</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                                        <CreditCard className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-gray-900 text-sm">ชำระเงินปลอดภัย</p>
                                        <p className="text-xs text-gray-500">รองรับทุกธนาคาร</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Seller Card */}
                        <div className="mt-4 bg-white rounded-2xl p-6 shadow-sm">
                            <Link
                                to={`/shop/${product.sellerId}`}
                                className="flex items-center gap-4 group"
                            >
                                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                    <span className="text-xl font-bold text-white">
                                        {shopName.charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-900 group-hover:text-red-600 transition-colors truncate">
                                        {shopName}
                                    </h4>
                                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                                        <div className="flex items-center gap-1">
                                            <Star size={14} className="text-yellow-400 fill-yellow-400" />
                                            <span>{product.sellerRating?.toFixed(1) || "0.0"}</span>
                                        </div>
                                        <span>•</span>
                                        <span>{product.reviewCount || 0} รีวิว</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-red-600 font-medium text-sm">
                                    <Store size={18} />
                                    <span className="hidden sm:inline">ไปที่ร้านค้า</span>
                                    <ChevronRight size={18} />
                                </div>
                            </Link>
                        </div>
                    </div>
                </div >

                {/* Product Description Section */}
                < div className="mt-8 bg-white rounded-2xl shadow-sm overflow-hidden" >
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h2 className="text-xl font-bold text-gray-900">รายละเอียดสินค้า</h2>
                    </div>
                    <div className="p-6">
                        <div
                            className="prose prose-gray max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-red-600"
                            dangerouslySetInnerHTML={{ __html: product.description || '<p class="text-gray-500">ไม่มีรายละเอียดสินค้า</p>' }}
                        />
                    </div>
                </div >

                {/* Reviews Section */}
                < div className="mt-8 bg-white rounded-2xl shadow-sm overflow-hidden" >
                    <div className="px-6 py-4 border-b border-gray-100">
                        <h2 className="text-xl font-bold text-gray-900">รีวิวจากผู้ซื้อ</h2>
                    </div>
                    <div className="p-6">
                        <ReviewsSection
                            userId={localStorage.getItem("userId")}
                            productId={productId}
                        />
                    </div>
                </div >
            </main >
        </div >
    );
};

export default ProductDetails;
